<template>
  <transition name="filter-positions-modal" appear>
    <div
      v-if="value"
      class="filter-positions-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-positions-modal-title"
      @mousedown.self="handleClose"
    >
      <div class="filter-positions-modal__card">
        <div class="filter-positions-modal__header">
          <span id="filter-positions-modal-title" class="filter-positions-modal__title">
            Filter positions
          </span>
          <button
            type="button"
            class="filter-positions-modal__close-btn"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="filter-positions-modal__filters-row">
          <div class="filter-positions-modal__column">
            <span class="filter-positions-modal__column-title">Status</span>
            <div class="filter-positions-modal__options">
              <CheckboxInput
                v-for="option in statusOptions"
                :key="option.value"
                :label="option.label"
                :value="statuses[option.value]"
                @input="updateStatus(option.value, $event)"
              />
            </div>
          </div>

          <div class="filter-positions-modal__column filter-positions-modal__column--radio">
            <RadioInput
              label="Include archived"
              :availableItems="archivedOptions"
              :checkedItem="includeArchived ? 'Yes' : 'No'"
              :uncheckedValue="archivedClearKey"
              @changeradio="onArchivedChange"
            />
          </div>
        </div>

        <!-- ER-10494 / MDP-551: single-select radio filter groups (Filter Modal Standard). -->
        <template v-if="showRadioGroups">
          <div class="filter-positions-modal__filters-row">
            <div
              v-if="showValidityRadioGroup"
              class="filter-positions-modal__column filter-positions-modal__column--radio"
            >
              <RadioInput
                label="Validity"
                :availableItems="validityOptions"
                :checkedItem="validityCheckedItem"
                @changeradio="onRadioGroupChange('validity', $event)"
              />
            </div>
            <div class="filter-positions-modal__column filter-positions-modal__column--radio">
              <RadioInput
                label="Occupied"
                :availableItems="occupiedOptions"
                :checkedItem="occupiedCheckedItem"
                @changeradio="onRadioGroupChange('occupied', $event)"
              />
            </div>
          </div>
          <div
            v-if="showPreparingForRadioGroup"
            class="filter-positions-modal__filters-row"
          >
            <div class="filter-positions-modal__column filter-positions-modal__column--radio">
              <RadioInput
                label="Preparing for"
                :availableItems="preparingForOptions"
                :checkedItem="preparingForCheckedItem"
                @changeradio="onRadioGroupChange('preparingFor', $event)"
              />
            </div>
          </div>
        </template>

        <!-- ER-10517 / MDP-558: Roles-attached radio group (net-new, no indicator card). -->
        <template v-if="showRolesAttachedRadioGroup">
          <div class="filter-positions-modal__filters-row">
            <div class="filter-positions-modal__column filter-positions-modal__column--radio">
              <RadioInput
                label="Roles-attached"
                :availableItems="rolesAttachedOptions"
                :checkedItem="rolesAttachedCheckedItem"
                @changeradio="onRadioGroupChange('rolesAttached', $event)"
              />
            </div>
          </div>
        </template>

        <div class="filter-positions-modal__divider" />

        <div class="filter-positions-modal__department">
          <span class="filter-positions-modal__column-title">Department</span>

          <DepartmentPickerField
            browse-list
            :value="department"
            :departments="departments"
            :loading="loading"
            @input="setDepartment"
            @search="onSearch"
            @clear="onClear"
          />

          <p v-if="!department" class="filter-positions-modal__hint">
            Leave empty to show positions across the whole organization.
          </p>

          <template v-else>
            <div
              class="filter-positions-modal__scope-toggle"
              role="radiogroup"
              aria-label="Department scope"
            >
              <button
                type="button"
                class="filter-positions-modal__scope-option"
                :class="{ 'filter-positions-modal__scope-option--active': scope === 'direct' }"
                role="radio"
                :aria-checked="scope === 'direct'"
                @click="setScope('direct')"
              >
                This department only
              </button>
              <span class="filter-positions-modal__scope-divider" aria-hidden="true" />
              <button
                type="button"
                class="filter-positions-modal__scope-option"
                :class="{ 'filter-positions-modal__scope-option--active': scope === 'subtree' }"
                role="radio"
                :aria-checked="scope === 'subtree'"
                @click="setScope('subtree')"
              >
                Include all sub-departments
              </button>
            </div>
            <p class="filter-positions-modal__hint">
              {{ scopeHint }}
            </p>
          </template>
        </div>

        <div class="filter-positions-modal__divider" />

        <div class="filter-positions-modal__footer">
          <button type="button" class="filter-positions-modal__reset" @click="handleReset">
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import DepartmentPickerField from "@components/DepartmentPickerField/DepartmentPickerField.vue";
import CheckboxInput from "@components/Checkbox/CheckboxInput.vue";
import RadioInput from "@components/Radio/RadioInput.vue";
import {
  DEFAULT_POSITION_RADIO_FILTER_PAYLOAD,
  POSITION_RADIO_GROUPS,
} from "./positionFilterPayload";
import { isProductReadinessEnabled } from "@/utils/accountProductFlags";

const props = defineProps({
  value: { type: Boolean, default: false },
  initialStatuses: {
    type: Object,
    default: () => ({ draft: true, active: true, retired: false }),
  },
  initialIncludeArchived: { type: Boolean, default: false },
  initialDepartment: { type: Object, default: null },
  initialScope: {
    type: String,
    default: "subtree",
    validator: (value) => ["direct", "subtree"].includes(value),
  },
  departments: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  initialRadioFilters: {
    type: Object,
    default: null,
    validator: (value) => {
      if (value === null) return true;
      const validKeys = ["validity", "occupied", "preparingFor", "rolesAttached"];
      return Object.entries(value).every(
        ([key, val]) => validKeys.includes(key) && typeof val === "string"
      );
    },
  },
  showRadioGroups: { type: Boolean, default: false },
  showRolesAttachedGroup: { type: Boolean, default: false },
});

const DEFAULT_STATUSES = { draft: true, active: true, retired: false };
const DEFAULT_INCLUDE_ARCHIVED = false;
const DEFAULT_SCOPE = "subtree";

const emit = defineEmits(["input", "close", "apply", "reset", "search", "clear"]);

const statusOptions = [
  { label: "Draft", value: "draft" },
  { label: "Active", value: "active" },
  { label: "Retired", value: "retired" },
];

const archivedOptions = [{ label: "No" }, { label: "Yes" }];
const archivedClearKey = ref("");

const statuses = ref({ ...DEFAULT_STATUSES, ...(props.initialStatuses || {}) });
const includeArchived = ref(props.initialIncludeArchived);
const department = ref(props.initialDepartment);
const scope = ref(props.initialScope);

// ER-10494 / MDP-551 radio filter groups. Selections are the human labels that the
// grid code persists and maps to the ER-10493 API params; see positionFilterPayload.js.
const validityOptions = POSITION_RADIO_GROUPS[0].items;
const occupiedOptions = POSITION_RADIO_GROUPS[1].items;
const preparingForOptions = POSITION_RADIO_GROUPS[2].items;
const rolesAttachedOptions = POSITION_RADIO_GROUPS[3].items;

const radioFilters = ref({
  ...DEFAULT_POSITION_RADIO_FILTER_PAYLOAD,
  ...(props.initialRadioFilters || {}),
});
const validityCheckedItem = computed(() => radioFilters.value.validity || "No Filter");
const occupiedCheckedItem = computed(() => radioFilters.value.occupied || "No Filter");
const preparingForCheckedItem = computed(() => radioFilters.value.preparingFor || "No Filter");
const rolesAttachedCheckedItem = computed(() => radioFilters.value.rolesAttached || "No Filter");

const showPreparingForRadioGroup = computed(
  () => props.showRadioGroups && isProductReadinessEnabled()
);

const showValidityRadioGroup = computed(
  () => props.showRadioGroups && isProductReadinessEnabled()
);

const showRolesAttachedRadioGroup = computed(
  () => props.showRolesAttachedGroup && isProductReadinessEnabled()
);

const scopeHint = computed(() =>
  scope.value === "subtree"
    ? "Showing this department plus every department nested beneath it, at any depth."
    : "Showing positions belonging directly to this department only."
);

watch(
  () => props.value,
  (open) => {
    if (open) {
      syncFromProps();
      emit("search", "");
    }
  },
  { immediate: true }
);

function syncFromProps() {
  statuses.value = { ...DEFAULT_STATUSES, ...(props.initialStatuses || {}) };
  includeArchived.value = props.initialIncludeArchived;
  department.value = props.initialDepartment;
  scope.value = props.initialScope;
  radioFilters.value = {
    ...DEFAULT_POSITION_RADIO_FILTER_PAYLOAD,
    ...(props.initialRadioFilters || {}),
  };
  if (!isProductReadinessEnabled()) {
    radioFilters.value = {
      ...radioFilters.value,
      preparingFor: "",
      validity: "",
      rolesAttached: "",
    };
  }
}

function updateStatus(key, checked) {
  statuses.value = { ...statuses.value, [key]: !!checked };
}

function onArchivedChange(payload) {
  includeArchived.value = payload?.value === "Yes";
}

function onRadioGroupChange(key, payload) {
  const value = payload?.value;
  const group = POSITION_RADIO_GROUPS.find((option) => option.key === key);
  if (!group) return;
  // Payload may carry the full label list (checkbox-style) or a single selection.
  const selected = Array.isArray(value)
    ? group.items.find((item) => item.value && value.includes(item.value))?.value || ""
    : value || "";
  radioFilters.value = { ...radioFilters.value, [key]: selected };
}

function setDepartment(dept) {
  department.value = dept;
  if (!dept) scope.value = DEFAULT_SCOPE;
}

function setScope(val) {
  scope.value = val;
}

function onSearch(query) {
  emit("search", query);
}

function onClear() {
  emit("clear");
  emit("search", "");
}

function handleReset() {
  statuses.value = { ...DEFAULT_STATUSES };
  includeArchived.value = DEFAULT_INCLUDE_ARCHIVED;
  department.value = null;
  scope.value = DEFAULT_SCOPE;
  radioFilters.value = { ...DEFAULT_POSITION_RADIO_FILTER_PAYLOAD };
  archivedClearKey.value = `reset-${Date.now()}`;
  emit("reset");
  emit("search", "");
}

function handleApply() {
  // Emit the radio filter selections when either radio-group area is visible.
  // Strip groups that are not shown so a stale persisted value is never applied
  // when its flag is off (MDP-551 vs MDP-558 gate different groups).
  let radioFiltersPayload = null;
  if (props.showRadioGroups || props.showRolesAttachedGroup) {
    radioFiltersPayload = { ...radioFilters.value };
    if (!props.showRadioGroups) {
      delete radioFiltersPayload.validity;
      delete radioFiltersPayload.occupied;
      delete radioFiltersPayload.preparingFor;
    }
    if (!props.showRolesAttachedGroup) {
      delete radioFiltersPayload.rolesAttached;
    }
    if (!isProductReadinessEnabled()) {
      delete radioFiltersPayload.preparingFor;
      delete radioFiltersPayload.validity;
      delete radioFiltersPayload.rolesAttached;
    }
  }

  emit("apply", {
    status: statusOptions
      .filter((option) => statuses.value[option.value])
      .map((option) => option.value),
    includeArchived: includeArchived.value,
    department: department.value,
    scope: department.value ? scope.value : null,
    radioFilters: radioFiltersPayload,
  });
}

function handleClose() {
  emit("input", false);
  emit("close");
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value) handleClose();
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.filter-positions-modal-enter-active,
.filter-positions-modal-leave-active {
  transition: opacity 180ms ease;
}

.filter-positions-modal-enter-from,
.filter-positions-modal-leave-to {
  opacity: 0;
}

.filter-positions-modal-enter-active .filter-positions-modal__card,
.filter-positions-modal-leave-active .filter-positions-modal__card {
  transition:
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease;
}

.filter-positions-modal-enter-from .filter-positions-modal__card,
.filter-positions-modal-leave-to .filter-positions-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .filter-positions-modal-enter-active,
  .filter-positions-modal-leave-active,
  .filter-positions-modal-enter-active .filter-positions-modal__card,
  .filter-positions-modal-leave-active .filter-positions-modal__card {
    transition: none !important;
  }
}

.filter-positions-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
  font-family: Inter, sans-serif;
}

.filter-positions-modal__card {
  width: 100%;
  max-width: 1032px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow:
    0 8px 8px -4px rgba(16, 24, 40, 0.03),
    0 20px 24px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.filter-positions-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-positions-modal__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.filter-positions-modal__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  cursor: pointer;
  color: #98a2b3;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.filter-positions-modal__close-btn:hover {
  background: rgba(152, 162, 179, 0.12);
}

.filter-positions-modal__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.filter-positions-modal__filters-row {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
}

.filter-positions-modal__column {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-positions-modal__column-title {
  padding-left: 8px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.filter-positions-modal__options {
  display: flex;
  flex-direction: column;
  padding-left: 8px;
}

.filter-positions-modal__divider {
  height: 1px;
  width: 100%;
  background: #e5e7eb;
}

.filter-positions-modal__department {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.filter-positions-modal__hint {
  margin: 0;
  color: #98a2b3;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
}

.filter-positions-modal__scope-toggle {
  display: flex;
  width: 100%;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  overflow: hidden;
}

.filter-positions-modal__scope-option {
  flex: 1 1 0;
  min-width: 0;
  appearance: none;
  border: 0;
  background: #fff;
  padding: 10px 16px;
  color: #344054;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  cursor: pointer;
  text-align: center;
}

.filter-positions-modal__scope-option--active {
  background: #f9f5ff;
  color: #6941c6;
}

.filter-positions-modal__scope-option:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}

.filter-positions-modal__scope-divider {
  width: 1px;
  align-self: stretch;
  background: #d0d5dd;
}

.filter-positions-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-positions-modal__reset {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  color: #475467;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  border-radius: 6px;
}

.filter-positions-modal__reset:hover {
  color: #344054;
  background: rgba(152, 162, 179, 0.08);
}

.filter-positions-modal__reset:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}
</style>
